import { useState, type ReactElement } from 'react';
import type { NetworkConnectionListItem } from '@kirin/api-client/contracts/shadow-ai';
import { confidenceLabelLabels } from '@kirin/api-client/contracts/shadow-ai';
import { useNetworkConnectionUsers } from '@/hooks/api/useShadowAiTools';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { EndpointDetailsPopover } from '@/components/Inventory/EndpointDetailsPopover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { cn } from '@/lib/utils';
import { formatBytes } from '../shared';

function IndicatorBadge({
  active,
  label,
}: {
  active: boolean | null | undefined;
  label: string;
}): ReactElement {
  return (
    <span
      className={cn(
        'px-2 py-0.5 rounded text-xs',
        active
          ? 'bg-blue-500/20 text-blue-400'
          : 'bg-muted text-muted-foreground/60'
      )}
    >
      {active ? '✓' : '−'} {label}
    </span>
  );
}

function CopyableCode({
  value,
  truncateAt = 16,
}: {
  value: string;
  truncateAt?: number;
}): ReactElement {
  const [copied, setCopied] = useState(false);
  const isTruncated = value.length > truncateAt;
  const displayValue = isTruncated
    ? `${value.substring(0, truncateAt)}...`
    : value;

  const handleCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!isTruncated) {
    return <code className="text-xs bg-muted px-2 py-1 rounded">{value}</code>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleCopy}
          className="text-xs bg-muted px-2 py-1 rounded font-mono cursor-pointer hover:bg-muted/80 transition-colors"
        >
          {copied ? '✓ Copied' : displayValue}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs break-all font-mono">
        {value}
        <div className="text-[10px] text-muted-foreground mt-1">
          Click to copy
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * KIR-3496: expanded-row detail for a Network Discovery domain — traffic
 * breakdown, TLS fingerprints, identification metadata, and the per-user table.
 * Ported from the old `ConnectionRow` expanded content.
 */
export function ExpandedConnectionRow({
  organizationId,
  row,
}: {
  organizationId: string;
  row: NetworkConnectionListItem;
}): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
  ]);
  const endpointEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  const { data: usersData } = useNetworkConnectionUsers(
    organizationId,
    row.domain,
    true
  );

  const confidence = row.confidence;
  const ja4Count = row.ja4Fingerprints?.length ?? 0;

  return (
    <div className="flex gap-8">
      <div className="space-y-3 shrink-0">
        <div className="flex items-center gap-2">
          <IndicatorBadge active={row.isStreaming} label="Streaming" />
          <IndicatorBadge active={row.isProgrammatic} label="Programmatic" />
          <IndicatorBadge active={row.isAsymmetric} label="Asymmetric" />
          <IndicatorBadge active={row.isLongLived} label="Long-lived" />
          {row.status === 'pending' && confidence && (
            <span className="text-xs text-muted-foreground ml-2">
              {confidence.score}% {confidenceLabelLabels[confidence.label]}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
          <div>
            <span className="text-muted-foreground">Traffic In:</span>{' '}
            {formatBytes(row.totalBytesIn)}
          </div>
          <div>
            <span className="text-muted-foreground">Traffic Out:</span>{' '}
            {formatBytes(row.totalBytesOut)}
          </div>
          <div>
            <span className="text-muted-foreground">Ratio:</span>{' '}
            {row.totalBytesOut > 0
              ? `${Math.round(row.totalBytesIn / row.totalBytesOut)}:1`
              : 'N/A'}
          </div>
          <div>
            <span className="text-muted-foreground">First seen:</span>{' '}
            {new Date(row.firstSeen).toLocaleDateString()}
          </div>
        </div>
        {ja4Count > 0 && (
          <div className="text-xs">
            <span className="text-muted-foreground">
              TLS Fingerprints ({ja4Count}):
            </span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {row.ja4Fingerprints.slice(0, 3).map(ja4 => (
                <CopyableCode key={ja4} value={ja4} truncateAt={20} />
              ))}
              {ja4Count > 3 && (
                <span className="text-muted-foreground">
                  +{ja4Count - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        {(row.status === 'approved' || row.status === 'flagged') && (
          <div className="text-xs">
            {row.identifiedAgentName && (
              <div className="mb-1">
                <span className="text-muted-foreground">Agent:</span>{' '}
                <span className="font-medium">{row.identifiedAgentName}</span>
              </div>
            )}
            {row.classifiedBy && (
              <div className="text-muted-foreground">
                Identified by{' '}
                {row.classifiedByName || row.classifiedByEmail || 'Unknown'}
                {row.classifiedAt &&
                  ` on ${new Date(row.classifiedAt).toLocaleDateString()}`}
              </div>
            )}
          </div>
        )}
      </div>

      {usersData && usersData.users.length > 0 && (
        <div className="flex-1 min-w-0 bg-muted rounded-lg p-3 border border-border">
          <div className="text-xs text-muted-foreground mb-2">
            Users ({usersData.users.length})
          </div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-1 pb-1 border-b border-border">
            <span className="w-28">User</span>
            <span className="w-24">
              {endpointEnabled ? 'Endpoint' : 'Machine'}
            </span>
            <span className="w-12">Hits</span>
            <span className="w-16">Traffic</span>
            <span className="w-24">JA4</span>
            <span>Last Seen</span>
          </div>
          <div className="space-y-1">
            {usersData.users.map(user => (
              <div
                key={`${user.userId}-${user.machineId}`}
                className="flex items-center gap-4 text-xs"
              >
                <span className="w-28 truncate">
                  {user.name || user.email || 'Unknown'}
                </span>
                <span className="w-24">
                  {endpointEnabled ? (
                    <EndpointDetailsPopover
                      userId={user.userId}
                      machineId={user.machineId}
                      endpointName={user.endpointName}
                    />
                  ) : (
                    <CopyableCode value={user.machineId} truncateAt={10} />
                  )}
                </span>
                <span className="w-12">{user.hitCount}</span>
                <span className="font-mono w-16">
                  {formatBytes((user.bytesIn ?? 0) + (user.bytesOut ?? 0))}
                </span>
                <span className="w-24">
                  {user.ja4 ? (
                    <CopyableCode value={user.ja4} truncateAt={10} />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </span>
                <span className="text-muted-foreground">
                  <RelativeTimeWithTooltip
                    date={user.lastSeen}
                    showIcon={false}
                    inline
                  />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
